import React, { useEffect, useState } from 'react';
import { Button, Flex, Tabs, Input } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { useAuthContext } from '../../contexts/AuthContext';
import { useErrorMessage } from '../../utils/errorMessage';
import { TemplateDocsDatatable } from './TemplateDocsDatatable';
import { ContentCustom, PageHeaderCustom } from '../../components';
import { routes } from '../../utils/constants/adminRoutes';

const { TabPane } = Tabs;
const { Search } = Input;

/**
 * `ListTemplateDocs` is a component that displays a list of template documents
 * categorized by use cases. It retrieves use cases from the server and displays
 * them in tabs, with each tab containing a datatable of documents for a particular
 * use case.
 *
 * It makes use of various hooks like `useTranslation` for internationalization,
 * `useNavigate` to navigate to other routes, and `useState` and `useEffect`
 * to manage the component's state and lifecycle respectively.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} type - The type of template document. Default value is an empty string.
 * @returns {React.Element} A JSX element containing a page header with a creation button and a set of tabs displaying documents categorized by use cases.
 *
 * @example
 *
 * return (
 *   <ListTemplateDocs />
 * );
 */
const ListTemplateDocs = ({ type }) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [useCases, setUsesCases] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [filterCases, setFilterCases] = useState(null);

  // The open tab lives in the URL so coming back from a document lands on the
  // tab it was opened from. Replacing rather than pushing keeps the back
  // button leaving the list instead of walking through the tabs.
  const activeUseCase = new URLSearchParams(location.search).get('uc');

  const handleTabChange = (useCase) => {
    const search = new URLSearchParams(location.search);
    search.set('uc', useCase);
    search.delete('p');
    navigate(
      { pathname: location.pathname, search: `?${search.toString()}` },
      { replace: true }
    );
  };

  const getUseCases = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: '/template-documents/enums'
      });
      setUsesCases(
        data.configurations[type].map((config) => ({
          label: config.use_case,
          key: config.use_case,
          children: (
            <TemplateDocsDatatable
              useCaseParams={config.use_case}
              type={type}
            />
          )
        }))
      );
    } catch (e) {
      message(e);
    }
  };

  const handleSearch = (value) => {
    const regex = new RegExp(`\\b${value}\\b`, 'i');
    const filterValues = (useCases || []).filter((useCase) =>
      regex.test(t(`template-documents.form.${useCase.label}`))
    );
    setFilterCases(filterValues);
    if (!value) {
      setFilterCases(null);
    }
  };

  useEffect(() => {
    (async () => {
      await getUseCases();
    })();
  }, []);

  const tabs = filterCases || useCases;
  // Falls back to the first tab while the URL carries none, and once the
  // search hides the open one.
  const activeKey =
    tabs.some(({ key }) => key === activeUseCase) && activeUseCase
      ? activeUseCase
      : tabs[0]?.key;

  return (
    <>
      <PageHeaderCustom title={t(`template-documents.title`)} />
      <ContentCustom>
        <Flex gap={16} justify="space-between" style={{ marginBottom: 16 }}>
          <Search
            allowClear
            placeholder={t('placeholder.search')}
            defaultValue=""
            onSearch={handleSearch}
            style={{ width: 240 }}
          />
          <Button onClick={() => navigate(`${routes.TEMPLATE_DOCS}/create`)}>
            <PlusOutlined />
            {t('buttons.create')}
          </Button>
        </Flex>
        <Tabs
          tabPosition="left"
          style={{ maxHeight: '100vh' }}
          activeKey={activeKey}
          onChange={handleTabChange}
        >
          {tabs.map(({ label, key, children }) => (
            <TabPane
              key={key}
              tab={t(`template-documents.form.${label}`)}
              style={{ paddingLeft: 0 }}
            >
              {children}
            </TabPane>
          ))}
        </Tabs>
      </ContentCustom>
    </>
  );
};

export default ListTemplateDocs;

ListTemplateDocs.propTypes = {
  type: PropTypes.string.isRequired
};
